/**
 * Chain configuration with USDC contract addresses.
 * USDC mainnet addresses sourced from Circle's official documentation.
 * All EVM USDC uses 6 decimals.
 */
import type { ChainId } from '@/types'

// ─── Interface ────────────────────────────────────────────────────────────────

export interface ChainConfig {
  chainId: ChainId
  name: string
  shortName: string
  nativeCurrency: { name: string; symbol: string; decimals: number }
  /** Primary RPC endpoint */
  rpcUrl: string
  /** Stellar Horizon URL — only set for Stellar chains */
  horizonUrl?: string
  blockExplorer: string
  /**
   * USDC ERC-20 contract address (EVM) or Circle's Stellar issuer account.
   * null when Circle has not deployed USDC on this chain.
   */
  usdcAddress: string | null
  /** Always 6 for EVM; Stellar tokens technically have 7 decimal places but
   *  displayed amounts use 6 to match Circle's canonical representation. */
  usdcDecimals: 6
  /** Hex color for UI chain badges */
  color: string
  /** Emoji icon */
  icon: string
  isTestnet?: boolean
}

// ─── Chain records ────────────────────────────────────────────────────────────

export const CHAIN_CONFIGS: Record<number | string, ChainConfig> = {
  // ── Ethereum Mainnet ───────────────────────────────────────────────────────
  1: {
    chainId: 1,
    name: 'Ethereum',
    shortName: 'ETH',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrl: 'https://ethereum-rpc.publicnode.com',
    blockExplorer: 'https://etherscan.io',
    // Circle USDC on Ethereum — https://developers.circle.com/stablecoins/docs/usdc-on-main-networks
    usdcAddress: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
    usdcDecimals: 6,
    color: '#627EEA',
    icon: 'Ξ',
  },

  // ── Base ───────────────────────────────────────────────────────────────────
  8453: {
    chainId: 8453,
    name: 'Base',
    shortName: 'BASE',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrl: 'https://mainnet.base.org',
    blockExplorer: 'https://basescan.org',
    // Circle native USDC on Base
    usdcAddress: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
    usdcDecimals: 6,
    color: '#0052FF',
    icon: '🔵',
  },

  // ── Polygon ────────────────────────────────────────────────────────────────
  137: {
    chainId: 137,
    name: 'Polygon',
    shortName: 'MATIC',
    nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
    rpcUrl: 'https://polygon-bor-rpc.publicnode.com',
    blockExplorer: 'https://polygonscan.com',
    // Circle native USDC on Polygon PoS (not bridged USDC.e)
    usdcAddress: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359',
    usdcDecimals: 6,
    color: '#8247E5',
    icon: '⬡',
  },

  // ── Arbitrum One ───────────────────────────────────────────────────────────
  42161: {
    chainId: 42161,
    name: 'Arbitrum One',
    shortName: 'ARB',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrl: 'https://arb1.arbitrum.io/rpc',
    blockExplorer: 'https://arbiscan.io',
    // Circle native USDC on Arbitrum One
    usdcAddress: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
    usdcDecimals: 6,
    color: '#28A0F0',
    icon: '🔷',
  },

  // ── Optimism ───────────────────────────────────────────────────────────────
  10: {
    chainId: 10,
    name: 'Optimism',
    shortName: 'OP',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrl: 'https://mainnet.optimism.io',
    blockExplorer: 'https://optimistic.etherscan.io',
    // Circle native USDC on Optimism
    usdcAddress: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85',
    usdcDecimals: 6,
    color: '#FF0420',
    icon: '🔴',
  },

  // ── Avalanche C-Chain ──────────────────────────────────────────────────────
  43114: {
    chainId: 43114,
    name: 'Avalanche',
    shortName: 'AVAX',
    nativeCurrency: { name: 'AVAX', symbol: 'AVAX', decimals: 18 },
    rpcUrl: 'https://api.avax.network/ext/bc/C/rpc',
    blockExplorer: 'https://snowscan.xyz',
    // Circle native USDC on Avalanche C-Chain
    usdcAddress: '0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E',
    usdcDecimals: 6,
    color: '#E84142',
    icon: '🔺',
  },

  // ── Fuji Testnet ───────────────────────────────────────────────────────────
  43113: {
    chainId: 43113,
    name: 'Fuji Testnet',
    shortName: 'FUJI',
    nativeCurrency: { name: 'AVAX', symbol: 'AVAX', decimals: 18 },
    rpcUrl: 'https://api.avax-test.network/ext/bc/C/rpc',
    blockExplorer: 'https://testnet.snowscan.xyz',
    // Circle testnet USDC on Fuji
    usdcAddress: '0x5425890298aed601595a70AB815c96711a31Bc65',
    usdcDecimals: 6,
    color: '#E84142',
    icon: '🔺',
    isTestnet: true,
  },

  // ── XDC Network ────────────────────────────────────────────────────────────
  50: {
    chainId: 50,
    name: 'XDC Network',
    shortName: 'XDC',
    nativeCurrency: { name: 'XDC', symbol: 'XDC', decimals: 18 },
    rpcUrl: 'https://erpc.xinfin.network',
    blockExplorer: 'https://explorer.xinfin.network',
    // Circle has not deployed native USDC on XDC mainnet
    usdcAddress: null,
    usdcDecimals: 6,
    color: '#2086B6',
    icon: '✕',
  },

  // ── XDC Apothem Testnet ─────────────────────────────────────────────────────
  51: {
    chainId: 51,
    name: 'XDC Apothem',
    shortName: 'XDC-T',
    nativeCurrency: { name: 'XDC', symbol: 'XDC', decimals: 18 },
    rpcUrl: 'https://erpc.apothem.network',
    blockExplorer: 'https://apothem.blocksscan.io',
    usdcAddress: null,
    usdcDecimals: 6,
    color: '#F49800',
    icon: '🔶',
    isTestnet: true,
  },

  // ── Base Sepolia Testnet ──────────────────────────────────────────────────
  84532: {
    chainId: 84532,
    name: 'Base Sepolia',
    shortName: 'BASE-T',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrl: 'https://sepolia.base.org',
    blockExplorer: 'https://sepolia.basescan.org',
    usdcAddress: '0x036CbD53842c5426634e7929541eC2318f3dCF7e',
    usdcDecimals: 6,
    color: '#0052FF',
    icon: '🔵',
    isTestnet: true,
  },

  // ── Stellar Mainnet ────────────────────────────────────────────────────────
  'stellar-mainnet': {
    chainId: 'stellar-mainnet',
    name: 'Stellar',
    shortName: 'XLM',
    nativeCurrency: { name: 'Lumens', symbol: 'XLM', decimals: 7 },
    rpcUrl: '',
    horizonUrl: 'https://horizon.stellar.org',
    blockExplorer: 'https://stellar.expert/explorer/public',
    // Circle's USDC issuer account on Stellar
    usdcAddress: 'GA5ZSEJYB37JRC5AVCIA5MOP4RHTM335X2KGX3IHOJAPP5RE34K4KZVN',
    usdcDecimals: 6,
    color: '#7FA7C9',
    icon: '✦',
  },

  // ── Stellar Testnet ────────────────────────────────────────────────────────
  'stellar-testnet': {
    chainId: 'stellar-testnet',
    name: 'Stellar Testnet',
    shortName: 'XLM-T',
    nativeCurrency: { name: 'Lumens', symbol: 'XLM', decimals: 7 },
    rpcUrl: '',
    horizonUrl: 'https://horizon-testnet.stellar.org',
    blockExplorer: 'https://stellar.expert/explorer/testnet',
    usdcAddress: null,
    usdcDecimals: 6,
    color: '#7FA7C9',
    icon: '✦',
    isTestnet: true,
  },
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function getChainConfig(chainId: ChainId | number | string): ChainConfig | null {
  return CHAIN_CONFIGS[chainId] ?? null
}

export function isEvmChain(chainId: ChainId): chainId is Exclude<ChainId, 'stellar-mainnet' | 'stellar-testnet'> {
  return typeof chainId === 'number'
}

export const EVM_CHAIN_IDS = [1, 8453, 137, 42161, 10, 43114, 43113, 50] as const
export type EvmChainId = (typeof EVM_CHAIN_IDS)[number]
